/**
 * Library of standard HR form templates.
 *
 * When an admin builds a Digital Form, they pick a preset from this
 * catalog (or "Start blank") and the FormBuilder loads with the
 * preset's schema pre-populated. Every field is editable + removable
 * and admins can add their own custom fields on top — there's no
 * "locked preset" concept; presets are just convenient starting points.
 *
 * Each preset is a fully-typed ``FormField[]`` so it round-trips
 * through the same builder/renderer/submission pipeline as a hand-
 * built form. Field IDs are deterministic strings (not the
 * dynamically-generated ``newFieldId()``) so a preset reused 100x
 * across orgs always produces the same schema shape.
 *
 * Adding a new preset: append a single ``FormPreset`` entry to
 * ``FORM_PRESETS``. No schema migration required because
 * form_schema is JSONB.
 */
import type { FormField } from "./types";

export interface FormPreset {
  /** Stable key — used in the picker grid + analytics. */
  key: string;
  /** Display name in the picker card. */
  name: string;
  /** One-sentence pitch shown under the name. */
  description: string;
  /** Suggested category for the parent template — admins can override. */
  category:
    | "onboarding"
    | "compliance"
    | "policy"
    | "benefits"
    | "tax"
    | "other";
  /** Whether the form would typically be REQUIRED to complete. */
  default_required?: boolean;
  /** Whether a signature field is part of the standard schema. */
  default_requires_signature?: boolean;
  /** Default deadline (days) — admins override in the parent settings. */
  default_deadline_days?: number;
  /** The actual FormField array. */
  schema: FormField[];
}


// ── Reusable atom builders ─────────────────────────────────────────


const sig = (id: string, label = "Employee Signature"): FormField => ({
  id,
  type: "signature",
  label,
  required: true,
  help_text:
    "By signing, you confirm the information above is accurate and complete.",
});

const sectionHeader = (id: string, label: string): FormField => ({
  id,
  type: "section_header",
  label,
});

const dateField = (
  id: string,
  label: string,
  required = true,
): FormField => ({
  id,
  type: "date",
  label,
  required,
});


// ── Preset catalog ─────────────────────────────────────────────────


export const FORM_PRESETS: FormPreset[] = [
  // ───────────── Onboarding ─────────────
  {
    key: "emergency_contact",
    name: "Emergency Contact",
    description:
      "Two emergency contacts kept on file for next-of-kin situations.",
    category: "onboarding",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 7,
    schema: [
      sectionHeader("section_primary", "Primary contact"),
      {
        id: "primary_name",
        type: "text",
        label: "Full name",
        required: true,
      },
      {
        id: "primary_relationship",
        type: "select",
        label: "Relationship",
        required: true,
        options: [
          "Spouse",
          "Parent",
          "Sibling",
          "Child",
          "Friend",
          "Other",
        ],
      },
      {
        id: "primary_phone",
        type: "phone",
        label: "Phone",
        required: true,
      },
      { id: "primary_email", type: "email", label: "Email" },
      sectionHeader("section_secondary", "Secondary contact (optional)"),
      { id: "secondary_name", type: "text", label: "Full name" },
      {
        id: "secondary_relationship",
        type: "select",
        label: "Relationship",
        options: [
          "Spouse",
          "Parent",
          "Sibling",
          "Child",
          "Friend",
          "Other",
        ],
      },
      { id: "secondary_phone", type: "phone", label: "Phone" },
      { id: "secondary_email", type: "email", label: "Email" },
      sig("signature"),
    ],
  },

  {
    key: "direct_deposit",
    name: "Direct Deposit Authorization",
    description:
      "Banking info for payroll. Routing + account numbers stored encrypted.",
    category: "tax",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 5,
    schema: [
      {
        id: "bank_name",
        type: "text",
        label: "Bank Name",
        required: true,
      },
      {
        id: "routing_number",
        type: "text",
        label: "Routing Number",
        required: true,
        help_text: "9 digits — usually on the bottom-left of your check.",
      },
      {
        id: "account_number",
        type: "text",
        label: "Account Number",
        required: true,
      },
      {
        id: "account_type",
        type: "radio",
        label: "Account Type",
        required: true,
        options: ["Checking", "Savings"],
      },
      {
        id: "deposit_amount",
        type: "radio",
        label: "Deposit Amount",
        required: true,
        options: ["Full Paycheck", "Specific Amount"],
      },
      {
        id: "specific_amount",
        type: "text",
        label: "Specific Amount (USD)",
        help_text:
          "Leave blank if you chose 'Full Paycheck' above.",
      },
      {
        id: "voided_check",
        type: "file_upload",
        label: "Voided Check (recommended)",
      },
      sig("signature"),
    ],
  },

  {
    key: "i9_section_1",
    name: "I-9 Section 1 (Employee Info)",
    description:
      "Federal Form I-9 attestation of employment eligibility.",
    category: "compliance",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 3,
    schema: [
      sectionHeader("section_personal", "Personal information"),
      { id: "last_name", type: "text", label: "Last name", required: true },
      {
        id: "first_name",
        type: "text",
        label: "First name",
        required: true,
      },
      { id: "middle_initial", type: "text", label: "Middle initial" },
      { id: "other_last_names", type: "text", label: "Other last names used" },
      { id: "address", type: "address", label: "Home address", required: true },
      dateField("date_of_birth", "Date of birth"),
      {
        id: "ssn",
        type: "ssn",
        label: "Social Security Number",
        required: true,
      },
      { id: "email", type: "email", label: "Email" },
      { id: "phone", type: "phone", label: "Phone" },
      sectionHeader(
        "section_attestation",
        "Citizenship / immigration status attestation",
      ),
      {
        id: "attestation",
        type: "radio",
        label: "I attest, under penalty of perjury, that I am:",
        required: true,
        options: [
          "A citizen of the United States",
          "A noncitizen national of the United States",
          "A lawful permanent resident",
          "A noncitizen authorized to work",
        ],
      },
      {
        id: "uscis_number",
        type: "text",
        label: "USCIS / A-Number (if applicable)",
        help_text:
          "Required for lawful permanent residents and authorized noncitizens.",
      },
      {
        id: "work_authorization_expiry",
        type: "date",
        label: "Work authorization expiry (if applicable)",
      },
      sig("signature"),
    ],
  },

  {
    key: "federal_tax_withholding",
    name: "Federal Tax Withholding (W-4 style)",
    description:
      "Federal income tax withholding election. Mirrors the IRS W-4 layout.",
    category: "tax",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 5,
    schema: [
      sectionHeader("section_step1", "Step 1 — Personal information"),
      {
        id: "full_name",
        type: "text",
        label: "Full legal name",
        required: true,
      },
      {
        id: "ssn",
        type: "ssn",
        label: "Social Security Number",
        required: true,
      },
      {
        id: "address",
        type: "address",
        label: "Home address",
        required: true,
      },
      {
        id: "filing_status",
        type: "radio",
        label: "Filing status",
        required: true,
        options: [
          "Single or Married filing separately",
          "Married filing jointly or Qualifying surviving spouse",
          "Head of household",
        ],
      },
      sectionHeader("section_step2", "Step 2 — Multiple jobs (optional)"),
      {
        id: "multiple_jobs",
        type: "checkbox",
        label:
          "I (and/or my spouse) have more than one job, or are a working spouse.",
        help_text:
          "Check this box if applicable; consult IRS Pub 505 for the worksheet.",
      },
      sectionHeader("section_step3", "Step 3 — Dependents (optional)"),
      {
        id: "dependents_under_17",
        type: "text",
        label: "Number of qualifying children under 17",
        help_text: "Enter as a whole number (or leave blank for 0).",
      },
      {
        id: "other_dependents",
        type: "text",
        label: "Number of other dependents",
      },
      sectionHeader(
        "section_step4",
        "Step 4 — Other adjustments (optional)",
      ),
      {
        id: "additional_withholding",
        type: "text",
        label: "Additional withholding per pay period (USD)",
        help_text: "Extra dollars to withhold each paycheck.",
      },
      {
        id: "extra_income",
        type: "text",
        label: "Other income (not from jobs)",
      },
      {
        id: "deductions",
        type: "text",
        label: "Deductions (other than standard deduction)",
      },
      sig("signature"),
    ],
  },

  {
    key: "background_check_consent",
    name: "Background Check Authorization",
    description:
      "Consent for pre-employment background screening.",
    category: "compliance",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 5,
    schema: [
      {
        id: "full_name",
        type: "text",
        label: "Full legal name",
        required: true,
      },
      {
        id: "other_names",
        type: "text",
        label: "Other names used (maiden, alias)",
      },
      dateField("date_of_birth", "Date of birth"),
      {
        id: "ssn",
        type: "ssn",
        label: "Social Security Number",
        required: true,
        help_text:
          "Required for criminal-record + employment-history checks.",
      },
      {
        id: "address",
        type: "address",
        label: "Current address",
        required: true,
      },
      {
        id: "previous_addresses",
        type: "textarea",
        label: "Previous addresses (last 7 years)",
        help_text: "One per line. Required for federal background checks.",
      },
      {
        id: "consent",
        type: "checkbox",
        label:
          "I authorize the company to conduct a background check, including criminal-record, employment, and education verification.",
        required: true,
      },
      sig("signature"),
    ],
  },

  {
    key: "drug_testing_consent",
    name: "Drug Testing Consent",
    description:
      "Consent for pre-employment and ongoing drug testing.",
    category: "compliance",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 5,
    schema: [
      {
        id: "full_name",
        type: "text",
        label: "Full legal name",
        required: true,
      },
      dateField("date_of_birth", "Date of birth"),
      sectionHeader("section_consent", "Consent"),
      {
        id: "pre_employment_consent",
        type: "checkbox",
        label:
          "I consent to a pre-employment drug screening as a condition of hire.",
        required: true,
      },
      {
        id: "random_consent",
        type: "checkbox",
        label:
          "I understand that random and post-incident drug tests may be required during my employment.",
        required: true,
      },
      {
        id: "current_medications",
        type: "textarea",
        label:
          "Current prescription medications (optional; will not affect hiring)",
        help_text:
          "Provided to the testing facility only — not stored in your HR record.",
      },
      sig("signature"),
    ],
  },

  {
    key: "beneficiary_designation",
    name: "Beneficiary Designation",
    description:
      "Beneficiary info for benefits, life insurance, and 401(k).",
    category: "benefits",
    default_required: false,
    default_requires_signature: true,
    default_deadline_days: 14,
    schema: [
      sectionHeader("section_primary", "Primary beneficiary"),
      {
        id: "primary_name",
        type: "text",
        label: "Full name",
        required: true,
      },
      {
        id: "primary_relationship",
        type: "select",
        label: "Relationship",
        required: true,
        options: ["Spouse", "Child", "Parent", "Sibling", "Estate", "Other"],
      },
      dateField("primary_dob", "Date of birth", false),
      {
        id: "primary_ssn",
        type: "ssn",
        label: "Social Security Number",
      },
      {
        id: "primary_percentage",
        type: "text",
        label: "Percentage allocation",
        required: true,
        help_text: "Whole numbers; primary + contingent must each total 100%.",
      },
      sectionHeader("section_contingent", "Contingent beneficiary (optional)"),
      { id: "contingent_name", type: "text", label: "Full name" },
      {
        id: "contingent_relationship",
        type: "select",
        label: "Relationship",
        options: ["Spouse", "Child", "Parent", "Sibling", "Estate", "Other"],
      },
      dateField("contingent_dob", "Date of birth", false),
      {
        id: "contingent_ssn",
        type: "ssn",
        label: "Social Security Number",
      },
      {
        id: "contingent_percentage",
        type: "text",
        label: "Percentage allocation",
      },
      sig("signature"),
    ],
  },

  // ───────────── Compliance ─────────────
  {
    key: "anti_harassment_acknowledgment",
    name: "Anti-Harassment Policy Acknowledgment",
    description:
      "Confirms the employee has read the anti-harassment policy and understands the reporting process.",
    category: "compliance",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 7,
    schema: [
      {
        id: "read_policy",
        type: "checkbox",
        label:
          "I have read and understand the company's Anti-Harassment and Anti-Discrimination Policy.",
        required: true,
      },
      {
        id: "zero_tolerance",
        type: "checkbox",
        label:
          "I understand the company has a zero-tolerance approach to harassment, discrimination, and retaliation of any kind.",
        required: true,
      },
      {
        id: "reporting_understanding",
        type: "checkbox",
        label:
          "I know how to report concerns — to my manager, HR, or through the anonymous reporting channel — and that I will not face retaliation for reporting in good faith.",
        required: true,
      },
      {
        id: "training_completed",
        type: "checkbox",
        label:
          "I have completed (or will complete) the required anti-harassment training.",
        required: true,
      },
      {
        id: "consequences_understood",
        type: "checkbox",
        label:
          "I understand that violating this policy may result in disciplinary action up to and including termination.",
        required: true,
      },
      {
        id: "questions",
        type: "textarea",
        label: "Questions or concerns about this policy (optional)",
      },
      sig("signature"),
    ],
  },

  {
    key: "at_will_employment_agreement",
    name: "At-Will Employment Agreement",
    description:
      "Confirms the at-will nature of employment in jurisdictions that recognize it.",
    category: "compliance",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 3,
    schema: [
      sectionHeader(
        "section_at_will",
        "At-will employment acknowledgment",
      ),
      {
        id: "at_will_understanding",
        type: "checkbox",
        label:
          "I understand my employment is at-will, meaning either I or the company may terminate the employment relationship at any time, with or without cause and with or without notice.",
        required: true,
      },
      {
        id: "no_contract_implied",
        type: "checkbox",
        label:
          "I understand no offer letter, handbook, policy, or verbal statement creates a contract for continued employment or guaranteed term.",
        required: true,
      },
      {
        id: "modification_authority",
        type: "checkbox",
        label:
          "I understand only an authorized officer of the company may alter the at-will relationship, and only in a written agreement signed by both parties.",
        required: true,
      },
      {
        id: "policies_subject_to_change",
        type: "checkbox",
        label:
          "I understand company policies, benefits, and compensation may be modified at the company's discretion.",
        required: true,
      },
      {
        id: "employee_full_name",
        type: "text",
        label: "Employee full legal name",
        required: true,
      },
      dateField("acknowledgment_date", "Date"),
      sig("signature"),
    ],
  },

  {
    key: "handbook_acknowledgment",
    name: "Employee Handbook Acknowledgment",
    description: "Confirms the employee has read and agrees to the handbook.",
    category: "compliance",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 7,
    schema: [
      {
        id: "read_handbook",
        type: "checkbox",
        label: "I have received and read the Employee Handbook in full.",
        required: true,
      },
      {
        id: "policies_understood",
        type: "checkbox",
        label:
          "I understand the policies on conduct, harassment, attendance, and confidentiality.",
        required: true,
      },
      {
        id: "agree_to_comply",
        type: "checkbox",
        label:
          "I agree to comply with the policies and understand violations may result in disciplinary action up to and including termination.",
        required: true,
      },
      {
        id: "questions",
        type: "textarea",
        label: "Questions about the handbook (optional)",
      },
      sig("signature"),
    ],
  },

  {
    key: "nda_acknowledgment",
    name: "NDA / Confidentiality Acknowledgment",
    description:
      "Lightweight confidentiality acknowledgment — pair with your full NDA.",
    category: "compliance",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 3,
    schema: [
      {
        id: "confidential_info_understanding",
        type: "checkbox",
        label:
          "I understand that I will have access to confidential and proprietary information during my employment.",
        required: true,
      },
      {
        id: "non_disclosure",
        type: "checkbox",
        label:
          "I will not disclose, share, or use confidential information for any purpose other than my work duties.",
        required: true,
      },
      {
        id: "post_employment",
        type: "checkbox",
        label:
          "I understand these obligations continue after my employment ends.",
        required: true,
      },
      sig("signature"),
    ],
  },

  {
    key: "conflict_of_interest",
    name: "Conflict of Interest Disclosure",
    description:
      "Annual disclosure of relationships or activities that might conflict.",
    category: "compliance",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 14,
    schema: [
      {
        id: "has_conflict",
        type: "radio",
        label:
          "Do you have any actual or potential conflicts of interest to disclose?",
        required: true,
        options: ["No conflicts to disclose", "Yes — see details below"],
      },
      {
        id: "conflict_details",
        type: "textarea",
        label: "Describe the conflict",
        help_text:
          "Include the parties involved, nature of the relationship, and any financial interest.",
      },
      {
        id: "outside_employment",
        type: "textarea",
        label: "Outside employment, board roles, or consulting arrangements",
      },
      {
        id: "family_relationships",
        type: "textarea",
        label:
          "Family or close personal relationships with employees, clients, or vendors",
      },
      sig("signature"),
    ],
  },

  // ───────────── Policy ─────────────
  {
    key: "remote_work_agreement",
    name: "Remote Work Agreement",
    description:
      "Expectations for hybrid / remote employees: workspace, hours, security.",
    category: "policy",
    default_required: false,
    default_requires_signature: true,
    default_deadline_days: 14,
    schema: [
      sectionHeader("section_arrangement", "Work arrangement"),
      {
        id: "work_arrangement",
        type: "radio",
        label: "Arrangement",
        required: true,
        options: ["Fully remote", "Hybrid", "Remote with travel"],
      },
      {
        id: "primary_work_location",
        type: "address",
        label: "Primary work location",
        required: true,
      },
      {
        id: "hours_per_week",
        type: "text",
        label: "Expected hours per week",
        required: true,
      },
      {
        id: "core_hours",
        type: "text",
        label: "Core working hours",
        help_text: "e.g. 9am – 3pm Pacific",
      },
      sectionHeader("section_setup", "Workspace + security"),
      {
        id: "dedicated_workspace",
        type: "checkbox",
        label:
          "I have a quiet, dedicated workspace appropriate for confidential calls.",
        required: true,
      },
      {
        id: "secure_internet",
        type: "checkbox",
        label:
          "I will use a secure (password-protected) internet connection.",
        required: true,
      },
      {
        id: "company_devices_only",
        type: "checkbox",
        label:
          "I will use company-issued devices for company work; no syncing to personal devices.",
        required: true,
      },
      sig("signature"),
    ],
  },

  // ───────────── Equipment + offboarding ─────────────
  {
    key: "equipment_acknowledgment",
    name: "Equipment Acknowledgment",
    description:
      "Records the equipment issued to a new hire and their care obligations.",
    category: "onboarding",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 7,
    schema: [
      sectionHeader("section_items", "Equipment received"),
      {
        id: "items",
        type: "checkbox_group",
        label: "Items issued",
        required: true,
        options: [
          "Laptop",
          "External monitor",
          "Keyboard",
          "Mouse",
          "Headset",
          "Mobile phone",
          "Office key / badge",
          "Other (note in comments)",
        ],
      },
      {
        id: "laptop_serial",
        type: "text",
        label: "Laptop serial number",
        help_text: "From the bottom of the device or About → Serial Number.",
      },
      {
        id: "phone_imei",
        type: "text",
        label: "Mobile device IMEI (if applicable)",
      },
      {
        id: "comments",
        type: "textarea",
        label: "Comments / additional items",
      },
      sectionHeader("section_attest", "Acknowledgment"),
      {
        id: "ack_company_property",
        type: "checkbox",
        label:
          "I understand this equipment is company property and will be returned upon separation.",
        required: true,
      },
      {
        id: "ack_reasonable_care",
        type: "checkbox",
        label:
          "I will use reasonable care and report damage / loss promptly.",
        required: true,
      },
      sig("signature"),
    ],
  },

  {
    key: "exit_interview",
    name: "Exit Interview Survey",
    description:
      "Structured offboarding feedback. Optional fields keep it low-friction.",
    category: "other",
    default_required: false,
    default_requires_signature: false,
    default_deadline_days: 14,
    schema: [
      {
        id: "primary_reason",
        type: "select",
        label: "Primary reason for leaving",
        required: true,
        options: [
          "New opportunity",
          "Compensation",
          "Career growth",
          "Work-life balance",
          "Manager / management",
          "Company culture",
          "Relocation",
          "Personal / family",
          "Retirement",
          "Other",
        ],
      },
      {
        id: "would_recommend",
        type: "radio",
        label: "Would you recommend this company as a place to work?",
        required: true,
        options: [
          "Strongly yes",
          "Yes",
          "Neutral",
          "No",
          "Strongly no",
        ],
      },
      {
        id: "highlights",
        type: "textarea",
        label: "What did you enjoy most about working here?",
      },
      {
        id: "improvements",
        type: "textarea",
        label: "What could the company improve?",
      },
      {
        id: "manager_feedback",
        type: "textarea",
        label: "Feedback for your manager (kept confidential)",
      },
      {
        id: "rehire_interest",
        type: "checkbox",
        label: "I would consider returning to this company in the future.",
      },
    ],
  },

  // ───────────── Self-service updates ─────────────
  {
    key: "address_change",
    name: "Address / Contact Update",
    description:
      "Employee-initiated update to mailing address, phone, and emergency contact.",
    category: "other",
    default_required: false,
    default_requires_signature: true,
    default_deadline_days: 7,
    schema: [
      {
        id: "new_address",
        type: "address",
        label: "New mailing address",
        required: true,
      },
      dateField("effective_date", "Effective date"),
      {
        id: "new_phone",
        type: "phone",
        label: "New phone number",
      },
      {
        id: "new_email",
        type: "email",
        label: "New personal email",
      },
      {
        id: "reason",
        type: "select",
        label: "Reason for update",
        options: [
          "Moved",
          "Phone number change",
          "Email change",
          "Other",
        ],
      },
      sig("signature"),
    ],
  },

  // ───────────── Generic ─────────────
  {
    key: "generic_acknowledgment",
    name: "Generic Acknowledgment",
    description:
      "Minimal one-checkbox-and-signature template. Edit the copy for your policy.",
    category: "policy",
    default_required: true,
    default_requires_signature: true,
    default_deadline_days: 7,
    schema: [
      {
        id: "acknowledgment",
        type: "checkbox",
        label:
          "I have read and understand the policy referenced in this document.",
        required: true,
      },
      {
        id: "comments",
        type: "textarea",
        label: "Comments (optional)",
      },
      sig("signature"),
    ],
  },
];


/** Convenience lookup. */
export function findPreset(key: string): FormPreset | undefined {
  return FORM_PRESETS.find((p) => p.key === key);
}
